import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '../lib/database.types'
import type {
  ContinueWatching,
  Genre,
  MeaningLang,
  ExamHistory,
  ExamKind,
  LevelReport,
  LevelReportCopy,
  ExamQuestion,
  ExamResult,
  Profile,
  QuizQuestion,
  ReviewCard,
  SavedWord,
  StudyToday,
  SubtitleLine,
  TopikLevel,
  Video,
  VideoScript,
  Word,
} from '../lib/types'
import { cleanMeaning, convertible, firstForm, registerOptions } from '../lib/exam'
import { isUuid } from '../lib/ids'
import { clampLevel, curationDistance } from '../lib/level'
import { EXAM_HISTORY_ATTEMPTS, EXAM_SIZE, MASTER_STREAK, REVIEW_INTERVALS } from '../lib/types'
import { ensureSession } from '../lib/supabase'
import type { Repository } from './repository'

type Row<T extends keyof Database['public']['Tables']> =
  Database['public']['Tables'][T]['Row']

function toVideo(r: Row<'videos'>): Video {
  return {
    id: r.id,
    youtubeId: r.youtube_id,
    title: r.title,
    titleEn: r.title_en,
    genre: r.genre as Genre,
    topikLevel: clampLevel(r.topik_level),
    durationSec: r.duration_sec,
    hasManualCc: r.has_manual_cc,
    status: r.status as Video['status'],
    newWordCount: r.new_word_count,
    vocabCoverage: r.vocab_coverage,
    slug: r.slug,
  }
}

/** jsonb 는 any 로 내려온다 — 문자열 값만 남겨 화면이 [object Object] 를 그리지 않게 한다. */
function toMeanings(r: { meanings?: unknown }): Partial<Record<MeaningLang, string>> {
  const raw = r.meanings
  if (!raw || typeof raw !== 'object') return {}
  const out: Partial<Record<MeaningLang, string>> = {}
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === 'string' && v.trim()) out[k as MeaningLang] = v
  }
  return out
}

type WordRow = Row<'words'> & {
  word_examples?: Array<{ sentence: string; translation_en: string }> | null
}

function toWord(r: WordRow): Word {
  return {
    id: r.id,
    lemma: r.lemma,
    meaningEn: r.meaning_en,
    meanings: toMeanings(r),
    topikLevel: clampLevel(r.topik_level),
    register: r.register as Word['register'],
    pronunciation: r.pronunciation,
    examForm: r.exam_form,
    tip: r.tip,
    // 단어당 하나만 보여준다. 여럿이면 첫 번째 — 고르는 기준이 아직 없다.
    example: r.word_examples?.[0]
      ? {
          sentence: r.word_examples[0].sentence,
          translationEn: r.word_examples[0].translation_en,
        }
      : null,
  }
}

function toLine(r: Row<'subtitle_lines'>): SubtitleLine {
  return {
    id: r.id,
    videoId: r.video_id,
    idx: r.idx,
    startSec: Number(r.start_sec),
    endSec: Number(r.end_sec),
    text: r.text,
    textEn: r.text_en,
    translations: toMeanings({ meanings: (r as { translations?: unknown }).translations }),
  }
}

export class SupabaseRepository implements Repository {
  readonly mode = 'supabase' as const

  private readonly client: SupabaseClient<Database>

  constructor(client: SupabaseClient<Database>) {
    this.client = client
  }

  private async userId(): Promise<string> {
    const id = await ensureSession(this.client)
    if (!id) throw new Error('세션을 만들 수 없습니다. Supabase Auth 설정(익명 로그인)을 확인하세요.')
    return id
  }

  async getProfile(): Promise<Profile> {
    const uid = await this.userId()
    const { data, error } = await this.client
      .from('profiles')
      .select('id, display_name, topik_level, preferred_genres, is_admin, meaning_lang, subtitle_mode')
      .eq('id', uid)
      .maybeSingle()
    if (error) throw error

    if (!data) {
      // 첫 진입 — 빈 프로필을 만들어 둔다 (레벨은 온보딩 완료 시 채움)
      const { error: insertError } = await this.client
        .from('profiles')
        .insert({ id: uid, display_name: null, topik_level: null })
      if (insertError) throw insertError
      return {
        id: uid,
        displayName: 'Learner',
        topikLevel: 3,
        preferredGenres: [],
        isAdmin: false,
        meaningLang: null,
        subtitleMode: null,
      }
    }

    return {
      id: data.id,
      displayName: data.display_name ?? 'Learner',
      topikLevel: data.topik_level ? clampLevel(data.topik_level) : 3,
      preferredGenres: (data.preferred_genres ?? []) as Genre[],
      isAdmin: data.is_admin,
      meaningLang: (data.meaning_lang ?? null) as Profile['meaningLang'],
      subtitleMode: (data.subtitle_mode ?? null) as Profile['subtitleMode'],
    }
  }

  async updateProfile(patch: Partial<Profile>): Promise<Profile> {
    const uid = await this.userId()
    const { error } = await this.client
      .from('profiles')
      .update({
        ...(patch.displayName !== undefined ? { display_name: patch.displayName } : {}),
        ...(patch.topikLevel !== undefined ? { topik_level: patch.topikLevel } : {}),
        ...(patch.preferredGenres !== undefined
          ? { preferred_genres: patch.preferredGenres }
          : {}),
        ...(patch.meaningLang !== undefined ? { meaning_lang: patch.meaningLang } : {}),
        ...(patch.subtitleMode !== undefined ? { subtitle_mode: patch.subtitleMode } : {}),
      })
      .eq('id', uid)
    if (error) throw error
    return this.getProfile()
  }

  async getQuizQuestions(): Promise<QuizQuestion[]> {
    const { data, error } = await this.client
      .from('quiz_questions')
      .select('*')
      .eq('is_active', true)
      .order('sort_order', { ascending: true })
    if (error) throw error

    return (data ?? []).map((r) => ({
      id: r.id,
      tag: r.tag,
      source: r.source,
      prompt: r.prompt,
      promptEn: r.prompt_en ?? '',
      options: r.options as QuizQuestion['options'],
      answerIdx: r.answer_idx,
      weightLevel: clampLevel(r.weight_level),
      isListening: (r as { is_listening?: boolean }).is_listening ?? false,
      audioUrl: (r as { audio_url?: string | null }).audio_url ?? null,
    }))
  }


  /**
   * 결과 리포트 구간. 맞힌 개수가 [min, max] 안에 드는 행 하나를 고른다.
   *
   * 병기 원칙에 따라 고른 언어와 영어를 함께 돌려준다 — 같은 언어면 alt 는 null 이다.
   */
  async getLevelReport(correctCount: number, total: number): Promise<LevelReport | null> {
    const { data, error } = await this.client
      .from('level_report_cases')
      .select('code, topik_level, copy, published_langs')
      .lte('min_correct', correctCount)
      .gte('max_correct', correctCount)
      .maybeSingle()
    if (error) throw error
    if (!data) return null

    /*
     * 언어는 MY 에서 고른 것을 따른다.
     *
     * 처음에는 고른 언어가 없으면 영어를 기본으로 삼았는데, 그러면 아무것도 고르지 않은
     * 사용자에게 **영어만** 나갔다. 앱의 기본은 한국어+영어 병기다 — 기본을 한국어로 두고
     * 영어를 아래에 병기한다. 고른 언어가 영어면 영어만 나가고(같은 문장을 두 번 보여줄
     * 이유가 없다), 아직 문구가 없는 언어(ja·zh·vi)를 골랐다면 한국어로 되돌아간다.
     */
    const row = data as unknown as {
      copy: Record<string, LevelReportCopy>
      published_langs: string[] | null
    }
    /*
     * 내보내기로 정한 언어만 쓴다. copy 에 번역이 있어도 목록에 없으면 고르지 않는다 —
     * 언어를 늘리거나 내리는 일이 DB 한 줄로 끝난다.
     *
     * 지금은 일·중·베까지 **초안 상태로** 내보낸다 (기획 결정). 시트로 읽는 것보다
     * 실제 화면에서 읽어야 어색한 문장이 드러나기 때문이다.
     */
    const reviewed = new Set(row.published_langs ?? ['ko'])
    const copyByLang = row.copy
    const pick = (l: string | null) => (l && reviewed.has(l) ? copyByLang[l] : undefined)

    const lang = (await this.getProfile().catch(() => null))?.meaningLang ?? null
    const primary = pick(lang) ?? pick('ko') ?? pick('en') ?? copyByLang.ko
    const en = pick('en') ?? null

    return {
      code: String((data as { code: string }).code),
      topikLevel: clampLevel(Number((data as { topik_level: number }).topik_level)),
      correctCount,
      total,
      copy: primary,
      copyAlt: en && en !== primary ? en : null,
    }
  }

  async saveQuizAttempt(answers: Array<number | null>, estimatedLevel: TopikLevel): Promise<void> {
    const uid = await this.userId()
    const { error } = await this.client
      .from('quiz_attempts')
      .insert({ user_id: uid, answers, estimated_level: estimatedLevel })
    if (error) throw error
    await this.updateProfile({ topikLevel: estimatedLevel })
  }

  async listCuratedVideos(level: TopikLevel, genre: Genre | 'all'): Promise<Video[]> {
    let q = this.client
      .from('videos')
      .select('*')
      .eq('status', 'published')
      .order('created_at', { ascending: false })
    if (genre !== 'all') q = q.eq('genre', genre)

    const { data, error } = await q
    if (error) throw error

    // 급수로 거르지 않고 가까운 순으로 올린다. 같은 거리면 최신순(위 order)이 유지된다.
    return (data ?? [])
      .map(toVideo)
      .sort((a, b) => curationDistance(a.topikLevel, level) - curationDistance(b.topikLevel, level))
  }

  /** 자막 + 토큰 매핑 + 사전을 영상 진입 시 1회만 가져온다 (N+1 방지). */
  async getVideoScript(videoId: string): Promise<VideoScript | null> {
    // 주소에는 slug 를 쓰지만 예전 링크(UUID)도 그대로 열려야 한다.
    const { data: videoRow, error: videoError } = await this.client
      .from('videos')
      .select('*')
      .eq(isUuid(videoId) ? 'id' : 'slug', videoId)
      .maybeSingle()
    if (videoError) throw videoError
    if (!videoRow) return null

    const { data: lineRows, error: lineError } = await this.client
      .from('subtitle_lines')
      .select('*')
      .eq('video_id', videoRow.id)
      .order('idx', { ascending: true })
    if (lineError) throw lineError

    const lines = (lineRows ?? []).map(toLine)
    if (lines.length === 0) {
      return { video: toVideo(videoRow), lines, words: {}, tokens: {} }
    }

    const { data: mapRows, error: mapError } = await this.client
      .from('line_words')
      .select('line_id, token, word_id, words(*, word_examples(sentence, translation_en))')
      .in(
        'line_id',
        lines.map((l) => l.id),
      )
    if (mapError) throw mapError

    const tokens: Record<number, Record<string, number>> = {}
    const words: Record<number, Word> = {}
    for (const row of (mapRows ?? []) as Array<
      Row<'line_words'> & { words: Row<'words'> | null }
    >) {
      ;(tokens[row.line_id] ??= {})[row.token] = row.word_id
      if (row.words) words[row.words.id] = toWord(row.words)
    }

    return { video: toVideo(videoRow), lines, words, tokens }
  }

  /**
   * 가장 최근에 보던 영상. 거의 다 본 것(95% 이상)은 "이어보기"가 아니므로 건너뛴다.
   * 그 영상이 비공개로 바뀌었으면 RLS 가 걸러 자연히 다음 것으로 넘어간다.
   */
  async getContinueWatching(): Promise<ContinueWatching | null> {
    await this.userId()
    const { data, error } = await this.client
      .from('watch_progress')
      .select('position_sec, videos(*)')
      .order('updated_at', { ascending: false })
      .limit(5)
    if (error) throw error

    for (const row of (data ?? []) as Array<{
      position_sec: number
      videos: Row<'videos'> | null
    }>) {
      if (!row.videos) continue
      const video = toVideo(row.videos)
      const pos = Number(row.position_sec)
      const percent = video.durationSec > 0 ? (pos / video.durationSec) * 100 : 0
      if (percent >= 95) continue
      return { video, positionSec: pos, percent: Math.min(100, Math.round(percent)) }
    }
    return null
  }

  async saveProgress(videoId: string, positionSec: number): Promise<void> {
    const uid = await this.userId()
    const { error } = await this.client.from('watch_progress').upsert(
      { user_id: uid, video_id: videoId, position_sec: positionSec, updated_at: new Date().toISOString() },
      { onConflict: 'user_id,video_id' },
    )
    if (error) throw error
  }

  async listSavedWords(): Promise<SavedWord[]> {
    await this.userId()
    const { data, error } = await this.client
      .from('saved_words')
      .select(
        'id, word_id, video_id, line_id, start_sec, mastered, created_at, words(*, word_examples(sentence, translation_en)), subtitle_lines(text, end_sec), videos(youtube_id)',
      )
      // 미암기를 먼저, 그 안에서 최신순 (스토리보드 UI 4 — 암기 완료는 하단으로)
      .order('mastered', { ascending: true })
      .order('created_at', { ascending: false })
    if (error) throw error

    return ((data ?? []) as Array<
      Row<'saved_words'> & {
        words: WordRow | null
        subtitle_lines: { text: string; end_sec: number | null } | null
        videos: { youtube_id: string | null } | null
      }
    >)
      .filter((r) => r.words)
      .map((r) => ({
        id: String(r.id),
        wordId: r.word_id,
        lemma: r.words!.lemma,
        meaningEn: r.words!.meaning_en,
        meanings: toMeanings(r.words!),
        topikLevel: clampLevel(r.words!.topik_level),
        sentence: r.subtitle_lines?.text ?? '',
        example: toWord(r.words!).example,
        videoId: r.video_id,
        youtubeId: r.videos?.youtube_id ?? null,
        lineId: r.line_id,
        startSec: Number(r.start_sec),
        endSec: r.subtitle_lines?.end_sec == null ? null : Number(r.subtitle_lines.end_sec),
        mastered: r.mastered,
        createdAt: r.created_at,
      }))
  }

  async saveWord(
    input: Omit<SavedWord, 'id' | 'createdAt' | 'mastered'>,
  ): Promise<{ saved: SavedWord; duplicate: boolean }> {
    const uid = await this.userId()
    // unique (user_id, word_id) — 이미 있으면 중복으로 처리한다.
    const { data, error } = await this.client
      .from('saved_words')
      .insert({
        user_id: uid,
        word_id: input.wordId,
        video_id: input.videoId,
        line_id: input.lineId,
        start_sec: input.startSec,
      })
      .select('id, created_at')
      .maybeSingle()

    if (error) {
      if (error.code === '23505') {
        return {
          saved: { ...input, id: `dup-${input.wordId}`, mastered: false, createdAt: '' },
          duplicate: true,
        }
      }
      throw error
    }

    return {
      saved: {
        ...input,
        id: String(data?.id ?? input.wordId),
        mastered: false,
        createdAt: data?.created_at ?? '',
      },
      duplicate: false,
    }
  }

  async setWordMastered(id: string, mastered: boolean): Promise<void> {
    await this.userId()
    const { error } = await this.client
      .from('saved_words')
      .update({ mastered, mastered_at: mastered ? new Date().toISOString() : null })
      .eq('id', Number(id))
    if (error) throw error
  }

  async deleteSavedWord(id: string): Promise<void> {
    await this.userId()
    const { error } = await this.client.from('saved_words').delete().eq('id', Number(id))
    if (error) throw error
  }

  /* --- 실력 점검 ------------------------------------------------------------ */

  /**
   * 문항을 그때그때 만든다. 문제 은행을 따로 두지 않는 이유는, 우리가 이미 가진
   * 데이터(급수별 어휘 · 시험용 예문 · 화계 TIP · 자막)가 곧 문제이기 때문이다.
   * 매번 다른 문항이 나오므로 외워서 푸는 것도 막힌다.
   */
  async getExamQuestions(level: TopikLevel, count = EXAM_SIZE): Promise<ExamQuestion[]> {
    const lo = Math.max(1, level - 1)
    const hi = Math.min(6, level + 1)

    const [vocab, examples, registers, listens] = await Promise.all([
      this.client
        .from('topik_vocab')
        .select('lemma, topik_level, meaning_en')
        .gte('topik_level', lo)
        .lte('topik_level', hi)
        .not('meaning_en', 'is', null)
        .limit(400),
      this.client
        .from('word_examples')
        .select('sentence, translation_en, words(lemma, topik_level)')
        .eq('reviewed', true)
        .limit(200),
      // 화계 문항 — 자막에 실제로 나온 반말 형태와 시험용 형태가 둘 다 있어야 성립한다
      this.client
        .from('line_words')
        .select(
          'token, words!inner(lemma, exam_form, tip, topik_level, register), subtitle_lines!inner(text)',
        )
        .not('words.exam_form', 'is', null)
        .limit(120),
      this.client
        .from('subtitle_lines')
        .select('text, start_sec, end_sec, videos!inner(youtube_id, topik_level)')
        .not('text_en', 'is', null)
        .not('videos.youtube_id', 'is', null)
        .limit(200),
    ])

    const qs: ExamQuestion[] = []
    const pick = <T,>(arr: T[], n: number) => shuffle([...arr]).slice(0, n)

    // ① 어휘 뜻 고르기
    type V = { lemma: string; topik_level: number; meaning_en: string }
    /*
     * 뜻풀이의 한국어 예시를 걷어낸다 — `store (e.g., 옷 가게)` 가 그대로 보기에
     * 들어가면 「가게」의 답이 보기에 적혀 있는 셈이다 (1,976개 중 78개가 이 꼴).
     * 걷어낸 뒤에도 그 단어가 남아 있으면 문항으로 쓰지 않는다.
     */
    const vs = ((vocab.data ?? []) as unknown as V[]).map((v) => ({
      ...v,
      meaning_en: cleanMeaning(v.meaning_en),
    }))
    const askable = vs.filter((v) => !v.meaning_en.includes(v.lemma))
    for (const v of pick(askable, 4)) {
      const wrong = pick(vs.filter((x) => x.lemma !== v.lemma && x.meaning_en !== v.meaning_en), 3)
      if (wrong.length < 3) continue
      const options = shuffle([v.meaning_en, ...wrong.map((w) => w.meaning_en)])
      qs.push({
        id: `m:${v.lemma}`,
        kind: 'meaning',
        prompt: '다음 단어의 뜻으로 알맞은 것을 고르십시오.',
        passage: v.lemma,
        options,
        answerIdx: options.indexOf(v.meaning_en),
        level: v.topik_level,
      })
    }

    // ② 예문 빈칸
    type E = { sentence: string; words: { lemma: string; topik_level: number } | null }
    const es = ((examples.data ?? []) as unknown as E[]).filter(
      (e) => e.words && e.sentence.includes(e.words.lemma),
    )
    for (const e of pick(es, 3)) {
      const lemma = e.words!.lemma
      const wrong = pick(es.filter((x) => x.words!.lemma !== lemma), 3).map((x) => x.words!.lemma)
      if (wrong.length < 3) continue
      const options = shuffle([lemma, ...wrong])
      qs.push({
        id: `c:${lemma}`,
        kind: 'cloze',
        prompt: '빈칸에 들어갈 말로 알맞은 것을 고르십시오.',
        passage: e.sentence.replace(lemma, '( ______ )'),
        options,
        answerIdx: options.indexOf(lemma),
        level: e.words!.topik_level,
      })
    }

    // ③ 화계 변환 — 오답에 자막의 반말 형태를 넣는다. 학습자가 실제로 하는 실수다.
    type R = {
      token: string
      words: {
        lemma: string
        exam_form: string
        tip: string | null
        topik_level: number
        register: string | null
      }
      subtitle_lines: { text: string }
    }
    /*
     * 아무 토큰이나 쓰면 문항이 성립하지 않는다. 두 가지를 거른다.
     *
     * ① 이미 격식체로 끝나는 문장 — "딱 걸렸습니다"에 대고 "시험 답안에 맞게 고치라"고
     *    물으면 고칠 것이 없다.
     * ② 종결형이 아닌 활용형 — "재미있게 보셨습니까?"의 '재미있게'를 '재미있습니다'로
     *    바꾸면 문장이 깨진다. exam_form 은 종결형이라 문장 끝에서만 맞바꿀 수 있다.
     *    다만 '애 → 아이', '딱 → 정확히'처럼 낱말을 통째로 바꾸는 구어체는 위치와
     *    무관하게 성립하므로 register 로 따로 통과시킨다.
     */
    const isFormalEnding = (t: string) => /(습니다|ㅂ니다|습니까|ㅂ니까|십시오|시오)[.!?]?$/.test(t.trim())
    const rs = ((registers.data ?? []) as unknown as R[]).filter((r) => {
      if (!r.words?.exam_form || !r.subtitle_lines?.text) return false
      const text = r.subtitle_lines.text.trim()
      if (isFormalEnding(text)) return false
      // 바꿀 수 없는 자리인가 — '-면'(연결어미)에 종결형을 물으면 답이 성립하지 않고,
      // '-ㄹ게'(의도)에 '지웁니다'(평서)를 답이라고 하면 뜻이 달라진다.
      if (!convertible(r.token, r.words.exam_form, text)) return false
      const sentenceFinal = new RegExp(`${escapeRe(r.token)}[.!?]?$`).test(text)
      return sentenceFinal || r.words.register === 'casual'
    })
    const seenLemma = new Set<string>()
    for (const r of pick(rs, 12)) {
      if (qs.filter((q) => q.kind === 'register').length >= 2) break
      if (seenLemma.has(r.words.lemma)) continue

      const answer = firstForm(r.words.exam_form)
      const built = registerOptions(
        answer,
        r.token,
        rs.filter((x) => x.words.lemma !== r.words.lemma).map((x) => x.words.exam_form),
        pick,
      )
      if (!built) continue
      const options = shuffle([...built])
      seenLemma.add(r.words.lemma)

      qs.push({
        id: `r:${r.words.lemma}`,
        kind: 'register',
        // 문장에 끼워 넣으라고 하지 않는다 — 낱말 교체형(애→아이)은 그대로 넣으면
        // 문장이 어그러진다. "이 말을 시험 답안에서는 어떻게 쓰나"를 묻는다.
        prompt: '〈 〉 안의 말을 시험 답안에서는 어떻게 씁니까?',
        passage: r.subtitle_lines.text.replace(r.token, `〈${r.token}〉`),
        options,
        answerIdx: options.indexOf(answer),
        explain: r.words.tip ?? undefined,
        level: r.words.topik_level,
      })
    }

    // ④ 듣고 고르기
    type L = {
      text: string
      start_sec: number | string
      end_sec: number | string | null
      videos: { youtube_id: string; topik_level: number }
    }
    const ls = ((listens.data ?? []) as unknown as L[]).filter((l) => l.text.length >= 6)
    for (const l of pick(ls, 1)) {
      const wrong = pick(ls.filter((x) => x.text !== l.text).map((x) => x.text), 3)
      if (wrong.length < 3) continue
      const options = shuffle([l.text, ...wrong])
      qs.push({
        id: `l:${l.text}`,
        kind: 'listening',
        prompt: '듣고 알맞은 것을 고르십시오.',
        youtubeId: l.videos.youtube_id,
        startSec: Number(l.start_sec),
        endSec: l.end_sec === null ? null : Number(l.end_sec),
        options,
        answerIdx: options.indexOf(l.text),
        level: l.videos.topik_level,
      })
    }

    return shuffle(qs).slice(0, count)
  }

  async saveExamResult(result: ExamResult): Promise<void> {
    try {
      const uid = await this.userId()
      await this.client.from('quiz_attempts').insert({
        user_id: uid,
        estimated_level: result.suggestedLevel,
        answers: { kind: 'exam', correct: result.correct, total: result.total, byKind: result.byKind },
      })
      void this.markStudyDay()
    } catch {
      /* 기록 실패가 점검을 막지 않는다 */
    }
  }

  /**
   * 최근 회차를 합쳐 종류별 정답률을 낸다.
   *
   * 새 테이블을 만들지 않는다 — 점검 결과는 이미 quiz_attempts 에 쌓이고 있었고,
   * 아무도 다시 읽지 않았을 뿐이다. 온보딩 진단도 같은 표에 들어가므로
   * answers.kind 로 갈라낸다.
   */
  async getExamHistory(limit = EXAM_HISTORY_ATTEMPTS): Promise<ExamHistory> {
    const uid = await this.userId()
    const { data, error } = await this.client
      .from('quiz_attempts')
      .select('answers, created_at')
      .eq('user_id', uid)
      .order('created_at', { ascending: false })
      .limit(50)
    if (error) throw error

    type Saved = {
      kind?: string
      byKind?: Array<{ kind: ExamKind; correct: number; total: number }>
    }
    const rows = (data ?? [])
      .map((r) => ({ a: r.answers as unknown as Saved, at: r.created_at }))
      .filter((r) => r.a?.kind === 'exam' && Array.isArray(r.a.byKind))
      .slice(0, limit)

    const acc = new Map<ExamKind, { correct: number; total: number }>()
    for (const r of rows) {
      for (const b of r.a.byKind ?? []) {
        const cur = acc.get(b.kind) ?? { correct: 0, total: 0 }
        cur.correct += b.correct
        cur.total += b.total
        acc.set(b.kind, cur)
      }
    }

    return {
      attempts: rows.length,
      lastAt: rows[0]?.at ?? null,
      areas: [...acc].map(([kind, v]) => ({ kind, ...v })),
    }
  }

  /* --- 복습 (간격 반복) ---------------------------------------------------- */

  /**
   * 오늘 복습할 단어를 문제로 만들어 준다.
   *
   * 문제 문장은 그 단어를 저장할 때 보던 자막 그대로다. 빈칸으로 만들 활용형은
   * line_words 에서 가져오는데, 자막을 고치면서 매핑이 끊어졌을 수 있다 —
   * 그런 카드는 빈칸을 만들 수 없으므로 조용히 건너뛴다.
   */
  async listDueReviews(limit = 10): Promise<ReviewCard[]> {
    const uid = await this.userId()
    const { day } = today()

    const { data, error } = await this.client
      .from('saved_words')
      .select(
        'id, word_id, line_id, start_sec, video_id, review_due, words(*), subtitle_lines(text, end_sec), videos(youtube_id)',
      )
      .eq('user_id', uid)
      .eq('mastered', false)
      .or(`review_due.is.null,review_due.lte.${day}`)
      .order('review_due', { ascending: true, nullsFirst: true })
      .limit(limit)
    if (error) throw error

    type SavedRow = Row<'saved_words'> & {
      words: Row<'words'> | null
      subtitle_lines: { text: string; end_sec: number | null } | null
      videos: { youtube_id: string | null } | null
    }
    const rows = ((data ?? []) as unknown as SavedRow[]).filter((r) => r.words && r.subtitle_lines)
    if (rows.length === 0) return []

    const [{ data: tokens }, { data: pool }] = await Promise.all([
      this.client
        .from('line_words')
        .select('line_id, word_id, token')
        .in('line_id', [...new Set(rows.map((r) => r.line_id))]),
      // 사전이 작아(수백 단어) 통째로 받아 오답을 고른다. 커지면 급수로 좁힌다.
      this.client.from('words').select('id, lemma, topik_level'),
    ])

    const tokenOf = new Map(
      (tokens ?? []).map((t) => [`${t.line_id}:${t.word_id}`, t.token as string]),
    )
    const candidates = (pool ?? []) as Array<{ id: number; lemma: string; topik_level: number }>

    const cards: ReviewCard[] = []
    for (const r of rows) {
      const sentence = r.subtitle_lines!.text
      const word = r.words!
      // 매핑이 끊어졌으면 표제어가 문장에 그대로 있는 경우만 살린다.
      const token = tokenOf.get(`${r.line_id}:${r.word_id}`) ?? (sentence.includes(word.lemma) ? word.lemma : null)
      if (!token || !sentence.includes(token)) continue

      const options = withDistractors(word.lemma, word.topik_level, word.id, candidates)
      cards.push({
        savedWordId: String(r.id),
        wordId: word.id,
        lemma: word.lemma,
        meaningEn: word.meaning_en,
        meanings: toMeanings(word),
        topikLevel: clampLevel(word.topik_level),
        sentence,
        token,
        startSec: Number(r.start_sec),
        endSec: r.subtitle_lines!.end_sec == null ? null : Number(r.subtitle_lines!.end_sec),
        videoId: r.video_id,
        youtubeId: r.videos?.youtube_id ?? null,
        options,
        answerIdx: options.indexOf(word.lemma),
      })
    }
    return cards
  }

  async submitReview(savedWordId: string, correct: boolean): Promise<void> {
    const uid = await this.userId()
    const { data: prev, error: readError } = await this.client
      .from('saved_words')
      .select('review_streak')
      .eq('id', Number(savedWordId))
      .maybeSingle()
    if (readError) throw readError

    const streak = correct ? (prev?.review_streak ?? 0) + 1 : 0
    const days = correct ? REVIEW_INTERVALS[Math.min(streak - 1, REVIEW_INTERVALS.length - 1)] : 1
    const due = new Date()
    due.setDate(due.getDate() + days)
    const mastered = correct && streak >= MASTER_STREAK
    const now = new Date().toISOString()

    const { error } = await this.client
      .from('saved_words')
      .update({
        review_streak: streak,
        review_due: due.toLocaleDateString('en-CA'),
        reviewed_at: now,
        // 연속 정답이 쌓이면 자동으로 암기 완료가 된다. 틀렸다고 되돌리지는 않는다 —
        // 사용자가 직접 체크한 것을 퀴즈가 지워버리면 남의 결정을 뒤엎는 셈이다.
        ...(mastered ? { mastered: true, mastered_at: now } : {}),
      })
      .eq('id', Number(savedWordId))
      .eq('user_id', uid)
    if (error) throw error
    void this.markStudyDay()
  }

  /**
   * 학습한 날을 하루 한 줄로 남긴다. 실패해도 학습을 막지 않는다 —
   * 스트릭은 부가 정보이고, 이것 때문에 단어 저장이 실패하면 본말이 뒤집힌다.
   */
  async markStudyDay(): Promise<void> {
    try {
      const uid = await this.userId()
      await this.client
        .from('study_days')
        .upsert({ user_id: uid, day: today().day }, { onConflict: 'user_id,day', ignoreDuplicates: true })
    } catch {
      /* 무시 */
    }
  }

  async getStudyToday(): Promise<StudyToday> {
    const uid = await this.userId()
    const { day, sinceIso } = today()

    const [days, watched, saved, mastered, reviewed, due] = await Promise.all([
      this.client
        .from('study_days')
        .select('day')
        .eq('user_id', uid)
        .order('day', { ascending: false })
        .limit(400),
      this.client
        .from('watch_progress')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', uid)
        .gte('updated_at', sinceIso),
      this.client
        .from('saved_words')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', uid)
        .gte('created_at', sinceIso),
      this.client
        .from('saved_words')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', uid)
        .gte('mastered_at', sinceIso),
      this.client
        .from('saved_words')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', uid)
        .gte('reviewed_at', sinceIso),
      this.client
        .from('saved_words')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', uid)
        .eq('mastered', false)
        .or(`review_due.is.null,review_due.lte.${day}`),
    ])

    return {
      streak: streakFrom((days.data ?? []).map((r) => r.day as string), day),
      watched: watched.count ?? 0,
      saved: saved.count ?? 0,
      mastered: mastered.count ?? 0,
      reviewed: reviewed.count ?? 0,
      due: due.count ?? 0,
    }
  }
}

/**
 * 오답 3개를 섞어 보기를 만든다. 급수가 가까운 단어를 먼저 고른다 —
 * 6급 단어 사이에 1급 단어가 끼면 답이 그냥 보인다.
 */
function withDistractors(
  answer: string,
  level: number,
  wordId: number,
  pool: Array<{ id: number; lemma: string; topik_level: number }>,
): string[] {
  const others = pool
    .filter((w) => w.id !== wordId && w.lemma !== answer)
    .sort((a, b) => Math.abs(a.topik_level - level) - Math.abs(b.topik_level - level))
  // 같은 거리 안에서는 매번 다른 오답이 나오게 섞는다.
  const near = others.slice(0, Math.min(others.length, 24))
  for (let i = near.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[near[i], near[j]] = [near[j], near[i]]
  }
  const options = [answer, ...near.slice(0, 3).map((w) => w.lemma)]
  for (let i = options.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[options[i], options[j]] = [options[j], options[i]]
  }
  return options
}

/** 토큰이 정규식 특수문자를 포함할 수 있다 (물음표·마침표) */
function escapeRe(v: string): string {
  return v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function shuffle<T>(a: T[]): T[] {
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

/** 사용자의 '오늘'. UTC 로 자르면 한국 시간 오전 9시 이전이 어제로 밀린다. */
function today(): { day: string; sinceIso: string } {
  const now = new Date()
  const start = new Date(now)
  start.setHours(0, 0, 0, 0)
  return { day: now.toLocaleDateString('en-CA'), sinceIso: start.toISOString() }
}

/**
 * 오늘(없으면 어제)부터 하루씩 거슬러 올라가며 연속으로 기록된 날을 센다.
 * 오늘 아직 학습하지 않았다고 어제까지의 연속을 0으로 만들면, 하루가 시작되자마자
 * 스트릭이 사라진 것처럼 보인다.
 */
function streakFrom(days: string[], todayStr: string): number {
  const set = new Set(days)
  const cursor = new Date(`${todayStr}T00:00:00`)
  if (!set.has(todayStr)) cursor.setDate(cursor.getDate() - 1)

  let n = 0
  for (;;) {
    const key = cursor.toLocaleDateString('en-CA')
    if (!set.has(key)) break
    n += 1
    cursor.setDate(cursor.getDate() - 1)
  }
  return n
}
